import { matchCustomers, type NameMatchLevel } from './matchCustomerNames';

/**
 * Which of ABB's SPAs a Division has not loaded, and, for a list of the
 * Division's customers, which SPA naming each one is missing from its account.
 *
 * Two files, joined on the SPA number (a VLOOKUP, as the pricing team does it
 * by hand):
 *
 * - ABB's SPA list ("ABB SPAS CUSTOMER INFO ONLY"): every SPA ABB holds,
 *   one line per SPA × Rexel branch × ABB end customer.
 * - The Results Workbook ("ALL - RESULTS - ABB"): what was loaded, one line
 *   per item, with the Division (tab letter), SPA number and the Rexel
 *   account it was loaded under.
 *
 * Neither file says which Division a branch is in. It is read from the loads:
 * a branch belongs to the Division its SPAs are most often loaded under.
 * Measured on the 2026-11-01 files, that puts exactly the 34 Ontario NEDCO
 * branches in N. Ontario (R), with Ottawa and Kanata under N. Quebec (O).
 *
 * An SPA loaded only under another Division is not a gap: a load cannot be
 * shared between banners, so it is reported apart, not as missing.
 */

export interface SpaListLine {
  quote: string;
  branch: string;
  quoteDate: string;
  validFrom: string;
  validTo: string;
  revision: string;
  endCustomerName: string;
  endCustomerNumber: string;
}

export interface LoadLine {
  division: string;
  quote: string;
  account: string;
  title: string;
}

export interface SoldToCustomer {
  name: string;
  /** Rexel's sold-to account number, as the Results Workbook's Account holds it. */
  number: string;
}

/** One SPA × ABB end customer, at the Division's branches. */
export interface DivisionSpa {
  quote: string;
  endCustomerName: string;
  endCustomerNumber: string;
  /** The Division's branches the SPA names for this end customer. */
  branches: string[];
  /** True when that is every branch of the Division. */
  allBranches: boolean;
  quoteDate: string;
  validFrom: string;
  validTo: string;
  revision: string;
  /** End customers the SPA names at the Division's branches. */
  endCustomerCount: number;
  /** Every Division the SPA is loaded under, in letter order. */
  loadedDivisions: string[];
  /** The Division's accounts the SPA is loaded under, as "TITLE (account)". */
  loadedAccounts: string[];
}

export type CustomerSpaStatus =
  /** Loaded under the customer's account. */
  | 'loaded'
  /** Loaded in the Division, under other accounts only. */
  | 'missing for account'
  /** Not loaded under any Division. */
  | 'not loaded'
  /** Loaded only under other Divisions: not shareable, so not a gap. */
  | 'other division';

export interface CustomerSpa {
  customer: SoldToCustomer;
  spa: DivisionSpa;
  match: NameMatchLevel;
  status: CustomerSpaStatus;
}

/** A customer with no SPA here whose name is on SPAs at other Divisions' branches. */
export interface CustomerWithoutSpa {
  customer: SoldToCustomer;
  /** The closest such end customer, if any: its name, SPAs and their branches' Divisions. */
  elsewhere: {
    endCustomerName: string;
    quotes: string[];
    divisions: string[];
  } | null;
}

export interface UnloadedSpaReport {
  division: string;
  /** The branches read as the Division's, in name order. */
  branches: string[];
  spaCount: number;
  loadedSpaCount: number;
  /** SPA × end customer lines not loaded under any Division. */
  notLoaded: DivisionSpa[];
  /** SPA × end customer lines loaded only under other Divisions: not shareable, not a gap. */
  otherDivision: DivisionSpa[];
  /** End customers of the group SPAs the Division loaded, beside the accounts it loaded them under. */
  loadedGroups: DivisionSpa[];
  /** The customer list's SPAs, missing ones first, other Divisions' last; empty without a list. */
  customerSpas: CustomerSpa[];
  /** Customers on the list whose name matches no end customer at the Division's branches. */
  customersWithoutSpa: CustomerWithoutSpa[];
}

function addTo<K, V>(map: Map<K, Set<V>>, key: K, value: V): void {
  const set = map.get(key);
  if (set) set.add(value);
  else map.set(key, new Set([value]));
}

/** Branch → the Division its SPA lines are most often loaded under. */
export function branchDivisions(
  spaLines: readonly SpaListLine[],
  loadedDivisions: ReadonlyMap<string, ReadonlySet<string>>,
): Map<string, string> {
  const counts = new Map<string, Map<string, number>>();
  for (const line of spaLines) {
    const divisions = loadedDivisions.get(line.quote);
    if (!divisions) continue;
    const byDivision = counts.get(line.branch) ?? new Map<string, number>();
    counts.set(line.branch, byDivision);
    for (const division of divisions) {
      byDivision.set(division, (byDivision.get(division) ?? 0) + 1);
    }
  }
  const result = new Map<string, string>();
  for (const [branch, byDivision] of counts) {
    // Most lines wins; a tie goes to the first letter, so the answer never
    // depends on file order.
    const [best] = [...byDivision].sort(
      ([a, countA], [b, countB]) => countB - countA || a.localeCompare(b),
    );
    if (best) result.set(branch, best[0]);
  }
  return result;
}

function sorted(values: Iterable<string>): string[] {
  return [...values].sort((a, b) => a.localeCompare(b));
}

const STATUS_ORDER: Record<CustomerSpaStatus, number> = {
  'not loaded': 0,
  'missing for account': 1,
  loaded: 2,
  'other division': 3,
};

/** What the Results Workbook loaded: per SPA, its Divisions, and the Division's accounts. */
interface LoadIndex {
  divisionsByQuote: Map<string, Set<string>>;
  accountsByQuote: Map<string, Set<string>>;
  /** "quote\0account" for every load under the Division. */
  quoteAccounts: Set<string>;
}

function indexLoads(lines: readonly LoadLine[], division: string): LoadIndex {
  const index: LoadIndex = {
    divisionsByQuote: new Map(),
    accountsByQuote: new Map(),
    quoteAccounts: new Set(),
  };
  for (const line of lines) {
    addTo(index.divisionsByQuote, line.quote, line.division);
    if (line.division !== division) continue;
    addTo(index.accountsByQuote, line.quote, `${line.title} (${line.account})`);
    index.quoteAccounts.add(`${line.quote}\u0000${line.account}`);
  }
  return index;
}

/** The SPA list at the Division's branches, one entry per SPA × end customer. */
function divisionSpas(
  lines: readonly SpaListLine[],
  branches: ReadonlySet<string>,
  loads: LoadIndex,
): DivisionSpa[] {
  const groups = new Map<
    string,
    { first: SpaListLine; branches: Set<string> }
  >();
  const endCustomersByQuote = new Map<string, Set<string>>();
  for (const line of lines) {
    if (!branches.has(line.branch)) continue;
    const key = `${line.quote}\u0000${line.endCustomerNumber}\u0000${line.endCustomerName}`;
    const group = groups.get(key);
    if (group) group.branches.add(line.branch);
    else groups.set(key, { first: line, branches: new Set([line.branch]) });
    addTo(endCustomersByQuote, line.quote, line.endCustomerNumber);
  }
  return [...groups.values()]
    .map(({ first, branches: named }) => ({
      quote: first.quote,
      endCustomerName: first.endCustomerName,
      endCustomerNumber: first.endCustomerNumber,
      branches: sorted(named),
      allBranches: named.size === branches.size,
      quoteDate: first.quoteDate,
      validFrom: first.validFrom,
      validTo: first.validTo,
      revision: first.revision,
      endCustomerCount: endCustomersByQuote.get(first.quote)?.size ?? 0,
      loadedDivisions: sorted(loads.divisionsByQuote.get(first.quote) ?? []),
      loadedAccounts: sorted(loads.accountsByQuote.get(first.quote) ?? []),
    }))
    .sort(
      (a, b) =>
        a.endCustomerName.localeCompare(b.endCustomerName) ||
        a.quote.localeCompare(b.quote),
    );
}

function customerStatus(
  spa: DivisionSpa,
  customer: SoldToCustomer,
  division: string,
  loads: LoadIndex,
): CustomerSpaStatus {
  if (loads.quoteAccounts.has(`${spa.quote}\u0000${customer.number}`)) {
    return 'loaded';
  }
  if (spa.loadedDivisions.includes(division)) return 'missing for account';
  return spa.loadedDivisions.length > 0 ? 'other division' : 'not loaded';
}

/** The customers' SPAs, matched by name, and which customers matched at all. */
function matchCustomerSpas(
  customers: readonly SoldToCustomer[],
  spas: readonly DivisionSpa[],
  division: string,
  loads: LoadIndex,
): { rows: CustomerSpa[]; matched: Set<number> } {
  // Matched by ABB end customer number: one ABB customer may be written
  // several ways ("CARON ELECTRIC - ON" / "CARON ELECTRIC ON").
  const candidates = new Map<string, string>();
  for (const spa of spas) {
    if (!candidates.has(spa.endCustomerNumber)) {
      candidates.set(spa.endCustomerNumber, spa.endCustomerName);
    }
  }
  const matches = matchCustomers(
    customers.map((customer, index) => ({
      id: String(index),
      name: customer.name,
    })),
    [...candidates].map(([id, name]) => ({ id, name })),
  );
  const rows: CustomerSpa[] = [];
  const matched = new Set<number>();
  for (const match of matches) {
    const index = Number(match.customerId);
    const customer = customers[index];
    if (!customer) continue;
    matched.add(index);
    for (const spa of spas) {
      if (spa.endCustomerNumber !== match.candidateId) continue;
      const status = customerStatus(spa, customer, division, loads);
      rows.push({ customer, spa, match: match.level, status });
    }
  }
  rows.sort(
    (a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      a.customer.name.localeCompare(b.customer.name) ||
      a.spa.quote.localeCompare(b.spa.quote),
  );
  return { rows, matched };
}

export function buildUnloadedSpaReport(input: {
  spaLines: readonly SpaListLine[];
  loadLines: readonly LoadLine[];
  division: string;
  customers: readonly SoldToCustomer[];
}): UnloadedSpaReport {
  const { division } = input;
  const loads = indexLoads(input.loadLines, division);
  const divisionOf = branchDivisions(input.spaLines, loads.divisionsByQuote);
  const branches: string[] = [];
  for (const [branch, of] of divisionOf) {
    if (of === division) branches.push(branch);
  }
  branches.sort((a, b) => a.localeCompare(b));
  const inDivision = new Set(branches);
  const spas = divisionSpas(input.spaLines, inDivision, loads);
  const loadedHere = (spa: DivisionSpa) =>
    spa.loadedDivisions.includes(division);
  const customerSpas = matchCustomerSpas(
    input.customers,
    spas,
    division,
    loads,
  );

  return {
    division,
    branches,
    spaCount: new Set(spas.map((spa) => spa.quote)).size,
    loadedSpaCount: new Set(
      spas.flatMap((spa) => (loadedHere(spa) ? [spa.quote] : [])),
    ).size,
    notLoaded: spas.filter((spa) => spa.loadedDivisions.length === 0),
    otherDivision: spas.filter(
      (spa) => spa.loadedDivisions.length > 0 && !loadedHere(spa),
    ),
    loadedGroups: spas.filter(
      (spa) => loadedHere(spa) && spa.endCustomerCount > 1,
    ),
    customerSpas: customerSpas.rows,
    customersWithoutSpa: withoutSpa(
      input.customers.filter((_, index) => !customerSpas.matched.has(index)),
      input.spaLines.filter((line) => !inDivision.has(line.branch)),
      divisionOf,
    ),
  };
}

/** Each customer with no SPA here, and the closest name on SPAs at other Divisions' branches. */
function withoutSpa(
  customers: readonly SoldToCustomer[],
  elsewhereLines: readonly SpaListLine[],
  divisionOf: ReadonlyMap<string, string>,
): CustomerWithoutSpa[] {
  const byNumber = new Map<
    string,
    { name: string; quotes: Set<string>; divisions: Set<string> }
  >();
  for (const line of elsewhereLines) {
    const entry = byNumber.get(line.endCustomerNumber) ?? {
      name: line.endCustomerName,
      quotes: new Set<string>(),
      divisions: new Set<string>(),
    };
    byNumber.set(line.endCustomerNumber, entry);
    entry.quotes.add(line.quote);
    const division = divisionOf.get(line.branch);
    if (division) entry.divisions.add(division);
  }
  const matches = matchCustomers(
    customers.map((customer, index) => ({
      id: String(index),
      name: customer.name,
    })),
    [...byNumber].map(([id, { name }]) => ({ id, name })),
  );
  return customers.map((customer, index) => {
    // matchCustomers sorts surest first.
    const best = matches.find((match) => match.customerId === String(index));
    const entry = best ? byNumber.get(best.candidateId) : undefined;
    return {
      customer,
      elsewhere: entry
        ? {
            endCustomerName: entry.name,
            quotes: sorted(entry.quotes),
            divisions: sorted(entry.divisions),
          }
        : null,
    };
  });
}
